import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { faAngleDown } from '@soundxyz/font-awesome/sharp-solid-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { GoldenEgg } from '@components/basics/GoldenEgg';
import { SongCard } from '@components/basics/SongCard';
import { StyledScroll } from '@components/main-components/containers/RadixScroll';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { GoldenEggSphereSM } from '@components/main-components/media/icons/GoldenEggSphereSM';
import type { FragmentType } from '@graphql/reactQuery';
import type { PlaylistInput } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { CollectionCardFragmentDoc } from '@graphql/reactQuery';
import { SPACE } from '@utils/constants';
import { ReleaseCardActions } from './ReleaseCardActions';
import { useReleaseCardInfo } from './useReleaseCardInfo';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment CollectionCard on CollectedRelease {
    id
    vipNftsCount
    gaNftsCount
    ownedTokens {
      isGoldenEgg
      serialNumber
      tierNumber
    }
    release {
      id
      goldenEggNft {
        id
        serialNumber
        owner {
          id
        }
      }
      goldenEggImage {
        id
        url
      }
      animatedGoldenEggImageOptimized {
        id
        url
      }
      vipCoverImage {
        id
        url
      }
      staticVipCoverImage {
        id
        url
      }
      gaCoverImage {
        id
        url
      }
      staticGaCoverImage {
        id
        url
      }
      ...ReleaseCardActions
      ...ReleasesCardInfo
    }
  }
`);

function formatTierNumber({ tierNumber }: { tierNumber: number | null }) {
  if (tierNumber === 0) {
    return `${SPACE}Forever` as const;
  } else if (tierNumber === 1) {
    return `${SPACE}Limited` as const;
  }

  return '';
}

export const CollectionCard = ({
  data,
  userId,
  playlist,
  showGiftOption,
}: {
  data: FragmentType<CollectionCardFragmentDoc>;
  userId: string;
  playlist: PlaylistInput;
  showGiftOption: boolean;
}) => {
  const [expanded, setExpanded] = React.useState(false);

  const { release, vipNftsCount, gaNftsCount, ownedTokens } = getFragment(
    CollectionCardFragmentDoc,
    data,
  );
  const hasVipNfts = vipNftsCount > 0;
  const hasGANfts = gaNftsCount > 0;

  const {
    vipCoverImage,
    staticVipCoverImage,
    gaCoverImage,
    staticGaCoverImage,
    goldenEggImage,
    animatedGoldenEggImageOptimized,
    goldenEggNft,
  } = release;

  const {
    coverImageUrl: fallBackUrl,
    trackUrl,
    onPlayClick,
    isAudioBuffering,
    playButtonPrefetchRef,
    isActiveTrack,
    isAlbum,
    hasAudioAccess,
    artistName,
    artistWebappPath,
    releaseTitle,
    currentListeningParty,
  } = useReleaseCardInfo({ data: release, playlist });

  const nonGoldenEggImage = (() => {
    if (hasVipNfts && !!vipCoverImage?.url) {
      return staticVipCoverImage?.url || vipCoverImage.url || fallBackUrl;
    }
    if (hasGANfts) {
      return staticGaCoverImage?.url || gaCoverImage?.url || fallBackUrl;
    }
    return fallBackUrl;
  })();

  const shouldRenderEdition = !!ownedTokens && ownedTokens.length > 1;
  const onClick = () => setExpanded(prev => !prev);

  const infoContainerBottom = shouldRenderEdition ? (
    <EditionInfo onClick={onClick}>
      {ownedTokens.length} Editions{' '}
      <div className={clsx('ml-1.5 hidden md:block', expanded ? 'rotate-180' : 'rotate-0')}>
        <FontAwesomeIcon icon={faAngleDown} />
      </div>
    </EditionInfo>
  ) : (
    <CollectionInfo>#{ownedTokens?.[0]?.serialNumber ?? 0}</CollectionInfo>
  );

  const hasGoldenEgg = !!goldenEggNft && goldenEggNft.owner.id === userId;

  const editionList = shouldRenderEdition ? (
    <EditionsList
      onClick={onClick}
      isExpanded={expanded}
      nfts={ownedTokens.map(({ serialNumber, isGoldenEgg, tierNumber }) => ({
        serialNumber,
        isGoldenEgg,
        tierNumber,
      }))}
    />
  ) : null;

  const coverImage = hasGoldenEgg
    ? animatedGoldenEggImageOptimized?.url || goldenEggImage.url
    : nonGoldenEggImage;

  return (
    <Container>
      {hasGoldenEgg && !expanded ? (
        <GoldenEggWrapper>
          <GoldenEgg />
        </GoldenEggWrapper>
      ) : null}
      <SongCard
        album={isAlbum}
        isPlaying={isActiveTrack}
        loading={false}
        edition={!!shouldRenderEdition}
        expanded={expanded}
        setExpanded={setExpanded}
      >
        <SongCard.Cover
          coverLink={trackUrl}
          image={<SongCard.Cover.Single src={coverImage} />}
          onPlayButtonClick={onPlayClick}
          isBufferingSong={isAudioBuffering}
          disablePlay={!hasAudioAccess}
          playButtonPrefetchRef={playButtonPrefetchRef}
          currentListeningParty={currentListeningParty}
        />
        <SongCard.Info
          header={
            <AccessibleLink absoluteUri={trackUrl} type="external">
              {releaseTitle}
            </AccessibleLink>
          }
          subheader={<AccessibleLink pathnameUri={artistWebappPath}>{artistName}</AccessibleLink>}
          actions={<ReleaseCardActions data={release} showGiftOption={showGiftOption} />}
          infoContainerBottom={infoContainerBottom}
          bottom={editionList}
        />
      </SongCard>
    </Container>
  );
};

const Container = styled('div', {
  position: 'relative',
  width: '100%',
});

const CollectionInfo = styled('div', {
  display: 'flex',
  height: '100%',
  alignItems: 'center',
});

const EditionInfo = styled('button', {
  all: 'unset',
  display: 'flex',
  height: '100%',
  alignItems: 'center',
  cursor: 'pointer',
  // This helps use increase the clickable area
  padding: 4,
  margin: -4,
  transition: 'transform 300ms cubic-bezier(0.87, 0, 0.13, 1)',
});

const GoldenEggWrapper = styled('div', {
  position: 'absolute',
  top: -20,
  right: 0,
  zIndex: '$above1',
  width: 60,

  // Fixes safari bug where golden egg disappears when scrolling
  // https://stackoverflow.com/questions/40895387/z-index-not-working-on-safari-fine-on-firefox-and-chrome
  '-webkit-transform': 'translate3d(0,0,0)',

  '@tablet': {
    width: 80,
  },
});

const EditionsList = ({
  isExpanded,
  nfts,
  onClick,
}: {
  isExpanded: boolean;
  nfts: { isGoldenEgg: boolean; serialNumber: number; tierNumber: number | null }[];
  onClick: () => void;
}) => {
  return (
    <div
      className={clsx('h-full overflow-hidden', isExpanded ? 'block' : 'hidden')}
      onClick={onClick}
    >
      <StyledOl
        className="scrollbar-dark block h-full w-full list-none overflow-y-auto overscroll-contain bg-black px-3"
        oldSongCard={false}
      >
        {nfts?.map(nft => (
          <div
            key={nft.serialNumber}
            className="relative border-b border-base700 py-[6px] text-left text-base-s text-base100 [&:last-of-type]:border-b-0"
          >
            #{nft.serialNumber}
            {formatTierNumber(nft)}
            {nft.isGoldenEgg ? (
              <div className="absolute right-0 top-1/2 flex -translate-y-1/2">
                <GoldenEggSphereSM width={50} height={40} />
              </div>
            ) : null}
          </div>
        ))}
      </StyledOl>
    </div>
  );
};

const StyledOl = styled('ol', {
  variants: {
    oldSongCard: {
      true: {
        ...StyledScroll,
      },
    },
  },
});
